import {Action, LazyDispatch, ReducerBuilder} from "redux-ts";
import {Reducer} from "platform/redux/Reducer";
import {TSMap} from "typescript-map";
import Utils from "platform/util/Utils";
import {OnAccountChanged, OnLoggedOut} from "core/redux/auth/AuthReduxActions";
import {FilterDateState, FilterDateType, FilterReduxState, FilterSymbolState} from "core/redux/filter/FilterReduxState";
import {
    PortfolioDateFilterPayload,
    ResetFilter, ResetFilterPayload,
    SetFilterVisible, SetFilterVisiblePayload,
    SetPortfolioDateFilter, UpdatePortfolioDateFilter,
    SetSymbolFilterContinent, SetSymbolFilterContinentPayload,
    SetMarketsStatus, SetMarketsStatusPayload,
    SetClosedTradesDefaultFilter, SetClosedTradesDefaultFilterPayload,
    SetSymbolFilterContinentType, ResetFilterType, OnFilterReset,
    SetHistoryOrderStatus, SetHistoryOrderStatusPayload,
} from "core/redux/filter/FilterReduxActions";
import {PortfolioType} from "enum/PortfolioType";
import {RouteType} from "core/router/Routes";
import DateTimeFormat from "core/format/DateTimeFormat";
import {SetPortfolioTab, SetPortfolioTabPayload} from "core/redux/portfolio/PortfolioReduxActions";
import * as moment from "moment-mini";
import Platform from "platform/Platform";
import {ServiceType} from "enum/ServiceType";
import FilterEngine from "core/engine/FilterEngine";
import {EntryOrderStatus} from "platform/protocol/trading/EntryOrderStatus";

export const EntryOrderFilterGroups: EntryOrderStatus[][] = [
    [EntryOrderStatus.Executed],
    [EntryOrderStatus.Cancelled, EntryOrderStatus.Rejected]
];

const initialState = (): FilterReduxState => {
    const defaultDateState: FilterDateState = {
        dateType: FilterDateType.All,
    };
    const portfolioDateFilter: TSMap<PortfolioType, FilterDateState> =  new TSMap();
    portfolioDateFilter.set(PortfolioType.OpenTrades, {...defaultDateState});
    portfolioDateFilter.set(PortfolioType.EntryOrders, {...defaultDateState});
    portfolioDateFilter.set(PortfolioType.ClosedTrades, {...defaultDateState, ...{
            dateType: FilterDateType.Today
        }});
    portfolioDateFilter.set(PortfolioType.ActivityLog, {...defaultDateState, ...{
            dateType: FilterDateType.Today
        }});
    portfolioDateFilter.set(PortfolioType.OrdersHistory, {...defaultDateState, ...{
        dateType: FilterDateType.ThisWeek,
        orderStatus: [EntryOrderStatus.Executed]
    }})
    return {
        portfolioDateFilter,
        symbolFilter: {
            continents: []
        }
    };
};

export default class FilterReducer extends Reducer<FilterReduxState> {

    private static _instance: FilterReducer;

    public static instance(): FilterReducer {
        return this._instance || (this._instance = new this());
    }

    private constructor() {
        super();
        const filterEngine: FilterEngine = Platform.engine(ServiceType.Filter);
        this._middlewareActions.set("@@router5/TRANSITION_START", filterEngine.onChangeRoute);
        this._middlewareActions.set(SetSymbolFilterContinentType, filterEngine.doSetSymbolFilterContinent);
        this._middlewareActions.set(ResetFilterType, filterEngine.doResetFilter);
    }

    public get name(): string {
        return "filter";
    }

    protected setup(builder: ReducerBuilder<FilterReduxState>): void {
        builder
            .init(initialState())
            .handle(OnLoggedOut, (state: FilterReduxState, action: Action<any>) => {
                return {
                    ...initialState(),
                    symbolFilter: state.symbolFilter
                };
            })
            .handle(OnAccountChanged, (state: FilterReduxState, action: Action<any>) => {
                return {
                    ...initialState()
                };
            })
            .handle(SetSymbolFilterContinent, (state: FilterReduxState, {payload}: Action<SetSymbolFilterContinentPayload>) => {
                const newState: FilterReduxState = Utils.merge({}, state);
                const filter: FilterSymbolState = {...newState.symbolFilter};
                filter.continents = payload.continents;
                newState.symbolFilter = filter;
                return newState;
            })
            .handle(SetMarketsStatus, (state: FilterReduxState, {payload}: Action<SetMarketsStatusPayload>) => {
                const newState: FilterReduxState = Utils.merge({}, state);
                const {marketStatus} = payload;
                const filter: FilterSymbolState = {...newState.symbolFilter};
                filter.marketStatus = marketStatus;
                newState.symbolFilter = filter;
                return newState;
            })
            .handle(SetFilterVisible, (state: FilterReduxState, {payload}: Action<SetFilterVisiblePayload>) => {
                const newState: FilterReduxState = Utils.merge({}, state);
                newState.filterVisible = payload.visible;
                if (payload.visible && payload.route === RouteType.MyTrades) {
                    const filter: FilterDateState = newState.portfolioDateFilter.get(payload.activePortfolioTab);
                    if (Utils.isNull(filter.from) || Utils.isNull(filter.to)) {
                        newState.portfolioDateFilter.set(payload.activePortfolioTab, Object.assign({}, filter, {
                            from: DateTimeFormat.now().startOf("day"),
                            to: DateTimeFormat.now().endOf("day")
                        }));
                    }
                }
                return newState;
            })
            .handle(SetPortfolioTab, (state: FilterReduxState, {payload}: Action<SetPortfolioTabPayload>) => {
                const filter: FilterDateState = state.portfolioDateFilter.get(payload.portfolioType);
                if (Utils.isNull(filter.from) || Utils.isNull(filter.to)) {
                    const newState: FilterReduxState = Utils.merge({}, state);
                    newState.portfolioDateFilter.set(payload.portfolioType, Object.assign({}, filter, {
                        from: DateTimeFormat.now().startOf("day"),
                        to: DateTimeFormat.now().endOf("day")
                    }));
                    return newState;
                }
                return state;
            })
            .handle(SetPortfolioDateFilter, (state: FilterReduxState, {payload}: Action<PortfolioDateFilterPayload>) => {
                return this.updatePortfolioFilter(state, payload.portfolioType, payload.dateType, payload.from, payload.to);
            })
            .handle(UpdatePortfolioDateFilter, (state: FilterReduxState, {payload}: Action<PortfolioDateFilterPayload>) => {
                return this.updatePortfolioFilter(state, payload.portfolioType, payload.dateType, payload.from, payload.to);
            })
            .handle(SetClosedTradesDefaultFilter, (state: FilterReduxState, {payload}: Action<SetClosedTradesDefaultFilterPayload>) => {
                return Object.assign({}, state, {
                    closedTradesDefaultFilter: payload.dateType,
                });
            })
            .handle(ResetFilter, (state: FilterReduxState, {payload}: Action<ResetFilterPayload>, dispatch: LazyDispatch) => {
                const newState: FilterReduxState = Utils.merge({}, state);
                const defaultDateType = payload.activePortfolioTab === PortfolioType.ClosedTrades ? state.closedTradesDefaultFilter :
                                        payload.activePortfolioTab === PortfolioType.OrdersHistory ? FilterDateType.ThisWeek :
                                        payload.activePortfolioTab === PortfolioType.ActivityLog ? FilterDateType.Today : FilterDateType.All;
                if (payload.route === RouteType.MyTrades || payload.route === RouteType.Filters) {
                    const filterDateState: FilterDateState = Object.assign({}, newState.portfolioDateFilter.get(payload.activePortfolioTab), {
                        dateType: defaultDateType,
                        from: DateTimeFormat.now().startOf("day"),
                        to: DateTimeFormat.now().endOf("day")
                    });
                    if (payload.activePortfolioTab === PortfolioType.OrdersHistory) {
                        filterDateState.orderStatus = [EntryOrderStatus.Executed];
                    }
                    newState.portfolioDateFilter.set(payload.activePortfolioTab, filterDateState);
                    dispatch(OnFilterReset({portfolioType: payload.activePortfolioTab, dateType: defaultDateType})).catch(() => {});
                } else if (payload.route === RouteType.Symbols) {
                    newState.symbolFilter = {
                        continents: []
                    };
                }
                return newState;
            })
            .handle(SetHistoryOrderStatus, (state: FilterReduxState, {payload}: Action<SetHistoryOrderStatusPayload>) => {
                const newState: FilterReduxState = Utils.merge({}, state);
                const filter = newState.portfolioDateFilter.get(PortfolioType.OrdersHistory);
                filter.orderStatus = payload.orderStatus;
                return newState;
            });
    }

    private updatePortfolioFilter(state: FilterReduxState, portfolioType: PortfolioType, dateType: FilterDateType, from: moment.Moment, to: moment.Moment): FilterReduxState {
        const newState: FilterReduxState = Utils.merge({}, state);
        const filterState: FilterDateState = Object.assign({}, newState.portfolioDateFilter.get(portfolioType), {
            dateType
        });
        if (from && to) {
            filterState.from = from.startOf("day");
            filterState.to = to.endOf("day");
            this._logger.debug("Set portfolio date filter for " + portfolioType + ". From: " + filterState.from + " To: " + filterState.to);
        }

        newState.portfolioDateFilter.set(portfolioType, filterState);
        return newState;
    }
}
